/**
 * @file Single source of truth for `{ page: { type, ids } }` — the shape
 * `AIProvider` ships in `AgentClientContext.page` and `InlineApproval`
 * (`MessagesList.tsx`) reads. Derived from `useParams()`/`useLocation()`, not
 * Valtio, so route changes stay live; anything unmatched resolves to
 * `fallback`, never a partial or unknown `PageType`. `getHasNavigatedAway`
 * stays a getter so no render-time `useSnapshot(agentLoopState)` re-renders
 * every `AIContext` consumer — it reads the raw proxy at call time, so call
 * it from a handler, never at render.
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { PageType, PageIds } from '#types';
import { agentLoopState } from '../components/agents/agentLoopState';

/**
 * Path prefixes inside `/settings/*` mapped to their `settingsX` page type.
 * Settings routes the agent has no business on (`user`, `organization`,
 * `security`, `api`) are deliberately absent — they resolve to `fallback`
 * and the panel is hidden by `Agent.tsx`.
 */
const SETTINGS_PAGE_TYPE: Array<{ prefix: string; type: PageType }> = [
    { prefix: 'layout-editor', type: 'settingsForms' },
    { prefix: 'entity-layout-editor', type: 'settingsRecords' },
    { prefix: 'tables-v2', type: 'settingsTables' },
    { prefix: 'workflow-editor', type: 'settingsWorkflows' },
];

/**
 * Derive `{ page, getHasNavigatedAway }` from the active route. `page` is
 * recomputed from the router hooks whenever any of them changes, which is the
 * only way its content can change; its identity is otherwise held stable so
 * `AIProvider`'s memoized handlers survive unrelated re-renders.
 * `getHasNavigatedAway` compares the running loop's bound ids against the
 * current route's, reading `agentLoopState` raw at call time.
 */
export function useAgentContext(): {
    page: { type: PageType; ids: PageIds };
    getHasNavigatedAway: () => boolean;
} {
    const { formId, versionId, entityId, recordId } = useParams<{
        formId: string;
        versionId: string;
        entityId: string;
        recordId: string;
    }>();
    const { pathname } = useLocation();

    const page = useMemo(
        () => derivePage({ formId, versionId, entityId, recordId, pathname }),
        [formId, versionId, entityId, recordId, pathname],
    );

    const getHasNavigatedAway = useCallback((): boolean => {
        const { runningSnapshot } = agentLoopState;
        return runningSnapshot !== null && !idsMatch(runningSnapshot, page.ids);
    }, [page.ids]);

    return { page, getHasNavigatedAway };
}

function derivePage(args: {
    formId?: string;
    versionId?: string;
    entityId?: string;
    recordId?: string;
    pathname: string;
}): { type: PageType; ids: PageIds } {
    const { formId, versionId, entityId, recordId, pathname } = args;

    if (pathname === '/') return { type: 'home', ids: {} };

    if (formId && versionId) {
        return { type: 'formVersion', ids: { formId, formVersionId: versionId } };
    }

    if (entityId && recordId) {
        return { type: 'record', ids: { entityId, recordId } };
    }

    if (entityId) {
        return { type: 'entity', ids: { entityId } };
    }

    if (formId) {
        return { type: 'forms', ids: { formId } };
    }

    if (pathname === '/forms' || pathname.startsWith('/forms/')) {
        return { type: 'forms', ids: {} };
    }

    if (pathname.startsWith('/settings/')) {
        const slug = pathname.split('/')[2] ?? '';
        const match = SETTINGS_PAGE_TYPE.find(s => slug === s.prefix);
        if (match) return { type: match.type, ids: {} };
    }

    return { type: 'fallback', ids: {} };
}

function idsMatch(a: PageIds, b: PageIds): boolean {
    return (
        a.formId === b.formId &&
        a.formVersionId === b.formVersionId &&
        a.entityId === b.entityId &&
        a.recordId === b.recordId
    );
}
